import {
  Badge,
  Flex,
  Image,
  Skeleton,
  Text,
  Title,
} from '@collinsonx/design-system/core';
import { getItem } from '@lib';
import { BOOKING_MODE } from 'config/constants';
import { BOOKING_MODE_STATE } from 'config/storage';
import { BookingContext } from 'context/BookingContext';
import useEntitlements from 'hooks/useEntitlements';
import useLocale from 'hooks/useLocale';
import useProduct from 'hooks/useProduct';
import React, { memo, useContext, useMemo } from 'react';
import { getCurrencySymbol } from 'utils/currencysymbol';

import classes from './LoungeInfo.module.css';

const LoungeInfo = ({
  hideImage = false,
  hideImageMobile = false,
  hidePrice = false, // temporal solution
  width = '100%',
}) => {
  const mode = getItem(BOOKING_MODE_STATE);
  const {
    airportName,
    currency,
    imageUrl,
    loadingProduct,
    loungeName,
    saleprice,
    terminal,
  } = useProduct();
  const { booking } = useContext(BookingContext);
  const {
    hasEntitlements,
    hasPermissionsForEntitlements,
    hasUnlimitedEntitlements,
    remainingEntitlementsCount,
    usedEntitlementsCount,
  } = useEntitlements();

  const { pendingPayment, selectedEntitlementsCount } = booking;

  const loungeLocation = useMemo(() => {
    if (!airportName) return '';

    return terminal ? `${airportName}, ${terminal}` : airportName;
  }, [airportName, terminal]);

  const translation = useLocale();

  const isCreateFormPage = window.location.pathname === '/';
  const isAmendFormPage = window.location.pathname === '/amend-booking';
  const isFormPage = isCreateFormPage || isAmendFormPage;

  const showPrice = !hidePrice && (pendingPayment || isFormPage);
  const showUnlimitedEntitlements =
    isFormPage && hasUnlimitedEntitlements && hasPermissionsForEntitlements;

  const getEntitlementsInfoState = () => {
    if (!hasPermissionsForEntitlements || !hasEntitlements)
      return {
        showEntitlementsBadge: false,
        showEntitlementsCount: false,
      };

    if (mode === BOOKING_MODE.CREATE)
      return {
        showEntitlementsBadge: Boolean(
          !isCreateFormPage && selectedEntitlementsCount
        ),
        showEntitlementsCount: Boolean(
          isCreateFormPage &&
            remainingEntitlementsCount &&
            !showUnlimitedEntitlements
        ),
      };

    if (mode === BOOKING_MODE.EDIT)
      return {
        showEntitlementsBadge: Boolean(
          !showUnlimitedEntitlements &&
            selectedEntitlementsCount &&
            ((isAmendFormPage && !pendingPayment) || !isAmendFormPage)
        ),
        showEntitlementsCount: Boolean(
          isAmendFormPage &&
            remainingEntitlementsCount &&
            pendingPayment &&
            !showUnlimitedEntitlements
        ),
      };

    return {
      showEntitlementsBadge: Boolean(usedEntitlementsCount),
      showEntitlementsCount: false,
    };
  };

  const { showEntitlementsBadge, showEntitlementsCount } =
    getEntitlementsInfoState();

  return (
    <Flex
      align="center"
      bg="#FFF"
      className={classes.container}
      direction={{ base: 'column', xl: 'row' }}
      gap={16}
      justify="center"
      p={24}
      w={width}
    >
      <div>
        <Skeleton visible={loadingProduct} width="100%">
          {!hideImage && imageUrl && (
            <Image
              alt="lounge image"
              className={classes.image}
              src={imageUrl}
              visibleFrom={hideImageMobile ? 'sm' : undefined}
            />
          )}
        </Skeleton>
      </div>
      <Flex
        className={classes.centerMobile}
        direction="column"
        gap={loadingProduct ? 16 : undefined}
        w="100%"
      >
        <Skeleton visible={loadingProduct}>
          <Title
            aria-label={translation.generic.labels.loungeName}
            fw={600}
            order={2}
            size={32}
          >
            {loungeName ?? '-'}
          </Title>
        </Skeleton>
        <Skeleton visible={loadingProduct}>
          <Text size="lg">{loungeLocation}</Text>
        </Skeleton>
        {showPrice && (
          <Skeleton visible={loadingProduct}>
            {saleprice && (
              <Flex className={classes.containerPrice} gap={6}>
                <Text className={classes.textPrice} fw={700}>
                  {getCurrencySymbol(currency ?? '')}
                  {parseFloat(saleprice.toString()).toFixed(2)}
                </Text>
                <Text size="xl" style={{ lineHeight: '50px' }}>
                  {translation.lounge.perPerson}
                </Text>
              </Flex>
            )}
          </Skeleton>
        )}
        {showEntitlementsBadge && (
          <Skeleton visible={loadingProduct}>
            <EntitlementUsedBadge />
          </Skeleton>
        )}
        {showUnlimitedEntitlements && (
          <Text size="lg">
            {translation.booking.entitlements.loungeInfo.unlimitedEntitlements}
          </Text>
        )}
        {showEntitlementsCount && (
          <Flex gap={8} justify="center" mt={10}>
            <Text size="lg" style={{ fontWeight: 'bold' }}>
              {remainingEntitlementsCount}
            </Text>
            <Text size="lg">
              {
                translation.booking.entitlements.loungeInfo
                  .entitlementsRemaining
              }
            </Text>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

const EntitlementUsedBadge = () => {
  const translation = useLocale();

  return (
    <Badge
      color="var(--mantine-color-gray-8)"
      mt={20}
      size="xl"
      style={{
        borderColor: 'var(--mantine-color-gray-2)',
        borderWidth: 2,
        fontSize: 14,
        fontWeight: 500,
        paddingX: 16,
        paddingY: 6,
        textTransform: 'none',
      }}
      variant="outline"
    >
      {translation.booking.entitlements.loungeInfo.entitlementsUsed}
    </Badge>
  );
};

export default memo(LoungeInfo);
